import React, { useEffect, useState, useRef } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Download, Sparkles, Calendar } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

export default function SummitSuccessPage() {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');
  const [status, setStatus] = useState(sessionId ? 'checking' : 'error');
  const [name, setName] = useState('');
  const [guideUrl, setGuideUrl] = useState('');
  const [tier, setTier] = useState('summit');
  const startedRef = useRef(false);

  useEffect(() => {
    if (!sessionId) return undefined;
    if (startedRef.current) return undefined;
    startedRef.current = true;

    const maxAttempts = 8;
    let attempt = 0;

    const poll = async () => {
      try {
        const res = await fetch(`${API}/api/summit-checkout/status/${sessionId}`);
        const data = await res.json();
        if (data.payment_status === 'paid') {
          setStatus('success');
          setName(data.name || '');
          setTier(data.tier || 'summit');
          if (data.guide_url) setGuideUrl(`${API}${data.guide_url}`);
          return;
        }
        if (data.status === 'expired') {
          setStatus('expired');
          return;
        }
        attempt += 1;
        if (attempt >= maxAttempts) {
          setStatus('timeout');
          return;
        }
        setTimeout(poll, 2000);
      } catch {
        setStatus('error');
      }
    };

    poll();
  }, [sessionId]);

  const first = (name || '').trim().split(' ')[0];

  return (
    <section className="mirror-page" data-testid="summit-success-page">
      <div className="mirror-intro" style={{ maxWidth: '600px' }}>
        {status === 'checking' && (
          <>
            <h1 className="mirror-title">Reserving your seat…</h1>
            <p className="mirror-description">Confirming your payment. One moment, beautiful.</p>
          </>
        )}

        {status === 'success' && (
          <>
            <h1 className="mirror-title" data-testid="summit-success-title">
              {tier === 'platinum'
                ? (first ? `Welcome to Platinum, ${first}!` : 'Welcome to Platinum!')
                : (first ? `You're in, ${first}!` : "You're in!")} {tier === 'platinum' ? '👑' : '🎉'}
            </h1>
            <p className="mirror-subtitle">
              {tier === 'platinum' ? 'Platinum Out Front · One Full Month' : 'Gangsta AI for Seniors · Live Summit'}
            </p>
            <p className="mirror-description" style={{ display: 'inline-flex', alignItems: 'center', gap: '0.5rem', justifyContent: 'center' }}>
              <Calendar size={16} strokeWidth={2} />
              {tier === 'platinum'
                ? 'Your summit seat is reserved for Saturday, September 12th — and Cabrini will reach out about your month ahead.'
                : 'Your seat is reserved for Saturday, September 12th.'}
            </p>
            <p className="mirror-description">
              Your full <strong>37-page illustrated guide</strong> is unlocked, and we&apos;ve sent a
              Congrats email with your download link. Grab it here anytime:
            </p>
            <div className="mirror-intro-actions" style={{ flexWrap: 'wrap' }}>
              {guideUrl && (
                <a
                  href={guideUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="village-btn village-btn--primary"
                  data-testid="summit-guide-download-btn"
                  style={{ display: 'inline-flex', alignItems: 'center', gap: '0.5rem' }}
                >
                  <Download size={18} strokeWidth={2.2} />
                  Download Your Guide
                </a>
              )}
              <a
                href="/gangsta-ai-seniors/"
                className="village-btn village-btn--secondary"
                data-testid="summit-enter-workroom-btn"
                style={{ display: 'inline-flex', alignItems: 'center', gap: '0.5rem' }}
              >
                <Sparkles size={16} strokeWidth={2} />
                Enter the Workroom
              </a>
            </div>
            <p style={{ marginTop: '1.25rem', fontSize: '0.78rem', color: 'rgba(245,236,215,0.55)', lineHeight: 1.6 }}>
              💡 Save your guide somewhere safe. Watch your inbox — we&apos;ll send your live
              access details as the summit gets closer. 🌙
            </p>
          </>
        )}

        {(status === 'error' || status === 'timeout' || status === 'expired') && (
          <>
            <h1 className="mirror-title">Something Went Sideways</h1>
            <p className="mirror-description">
              {status === 'expired'
                ? 'Your checkout session has expired. Please try reserving again.'
                : 'We could not confirm your payment yet. If you were charged, please contact us — we will sort it out.'}
            </p>
            <div className="mirror-intro-actions">
              <Link to="/summit" className="village-btn village-btn--primary" data-testid="summit-retry-btn">
                Back to the Summit
              </Link>
            </div>
          </>
        )}
      </div>
    </section>
  );
}
